// Import necessary components and hooks from Agora SDK and React
import {
  LocalVideoTrack,
  RemoteUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  useRTCClient
} from 'agora-rtc-react'
import { PropsWithChildren, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CameraIcon } from '@/components/Consultation/steps/VideoCall/icons/CameraIcon'
import { HangupIcon } from '@/components/Consultation/steps/VideoCall/icons/HangupIcon'
import { LoadingAnimation } from '@/components/Consultation/steps/VideoCall/icons/LoadingAnimation'
import { MicIcon } from '@/components/Consultation/steps/VideoCall/icons/MicIcon'
import { SwapIcon } from '@/components/Consultation/steps/VideoCall/icons/SwapIcon'
import { UserIcon } from '@/components/Consultation/steps/VideoCall/icons/UserIcon'
import { Modal } from '@/components/Modal'
import { useConsultation } from '@/hooks/useConsultation'
import { getProfessionalKey } from '@/locale/utils'
import { cn } from '@/utils/tailwind'

import { agoraClient } from './agora-config'
import { AgoraProvider } from './AgoraContext'
import { CallButton } from './CallButton'

const RemoteMediaStatusIndicator = ({ children }: PropsWithChildren) => (
  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#f4f4f4]/33 backdrop-blur-[3px]">{children}</span>
)

enum FACING_MODE {
  USER = 'user',
  ENVIRONMENT = 'environment'
}

// AgoraManager component responsible for handling Agora-related logic and rendering UI
const AgoraManager = ({
  config,
  handleHangUp
}: {
  config: {
    token: string
    channel: string
    uid: number
    appid: string
  }
  handleHangUp: () => Promise<void>
}) => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [micEnabled, setMicEnabled] = useState(true)
  const [cameraEnabled, setCameraEnabled] = useState(true)
  const [facingMode, setFacingMode] = useState<FACING_MODE>(FACING_MODE.USER)
  const agoraEngine = useRTCClient(agoraClient)
  const { consultation } = useConsultation()
  const professional = consultation?.professional

  const { isLoading: isLoadingCam, localCameraTrack } = useLocalCameraTrack(true, {
    facingMode
  })
  const { isLoading: isLoadingMic, localMicrophoneTrack } = useLocalMicrophoneTrack(true, {})
  const remoteUsers = useRemoteUsers(agoraClient)

  usePublish([localMicrophoneTrack, localCameraTrack], true, agoraClient)

  useJoin(config, true, agoraClient)

  useEffect(() => {
    return () => {
      localCameraTrack?.close()
      localMicrophoneTrack?.close()
    }
  }, [])

  const toggleMic = () => {
    setMicEnabled((prev) => !prev)
  }

  const toggleCamera = () => {
    setCameraEnabled((prev) => !prev)
  }

  const swapCamera = () => {
    setFacingMode((prev) => (prev === FACING_MODE.USER ? FACING_MODE.ENVIRONMENT : FACING_MODE.USER))
  }

  const hangUp = async () => {
    setLoading(true)
    await handleHangUp()
    setLoading(false)
  }

  useEffect(() => {
    localMicrophoneTrack?.setEnabled(micEnabled)
  }, [micEnabled])

  useEffect(() => {
    localCameraTrack?.setEnabled(cameraEnabled)
  }, [cameraEnabled])

  useEffect(() => {
    localCameraTrack?.setDevice({
      facingMode
    })
  }, [facingMode])

  useEffect(() => {
    setLoading(isLoadingCam || isLoadingMic)
  }, [isLoadingCam, isLoadingMic])

  if (loading || agoraEngine.connectionState !== 'CONNECTED') {
    return (
      <div className="grid w-full grow place-items-center">
        <LoadingAnimation className="text-primary h-32 w-32" />
      </div>
    )
  }

  const remoteUser = remoteUsers[0] || null

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

  return (
    <>
      <AgoraProvider localCameraTrack={localCameraTrack} localMicrophoneTrack={localMicrophoneTrack}>
        <div id="videos" className="animate-fade-in relative flex grow">
          {remoteUser ? (
            <>
              <div className={cn('absolute top-0 left-0 h-full w-full', !remoteUser.hasVideo && 'hidden')} key={remoteUser.uid}>
                <RemoteUser user={remoteUser} playVideo={remoteUser.hasVideo} playAudio={remoteUser.hasAudio} />
              </div>

              <div className="absolute top-4 left-4 z-10 flex flex-col items-start gap-2">
                {professional && (
                  <span className="rounded-full bg-[#f4f4f4]/33 px-4 py-2 backdrop-blur-[3px]">
                    {t(getProfessionalKey(professional), { name: professional.name })}
                  </span>
                )}
                <div className="flex gap-2">
                  {!remoteUser.hasAudio && (
                    <RemoteMediaStatusIndicator>
                      <MicIcon className="h-6 w-6" enabled={false} />
                    </RemoteMediaStatusIndicator>
                  )}
                  {!remoteUser.hasVideo && (
                    <RemoteMediaStatusIndicator>
                      <CameraIcon className="h-6 w-6" enabled={false} />
                    </RemoteMediaStatusIndicator>
                  )}
                </div>
              </div>

              {!remoteUser?.hasVideo && (
                <UserIcon className="text-primary absolute top-1/2 left-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 transform" />
              )}
            </>
          ) : (
            <div className="grid w-full grow place-items-center">
              <LoadingAnimation className="text-primary h-32 w-32" />
            </div>
          )}

          <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
            <div className="flex gap-4 rounded-[50px] bg-white/33 p-2 shadow-sm backdrop-blur-[3px]">
              <CallButton onClick={toggleMic} enabled={micEnabled}>
                <MicIcon className={micEnabled ? 'text-black' : 'text-black/50'} enabled={micEnabled} />
              </CallButton>
              <CallButton onClick={toggleCamera} enabled={cameraEnabled}>
                <CameraIcon className={cameraEnabled ? 'text-black' : 'text-black/50'} enabled={cameraEnabled} />
              </CallButton>
              {isMobile && (
                <CallButton onClick={swapCamera}>
                  <SwapIcon className="text-black" />
                </CallButton>
              )}
              <CallButton onClick={() => setIsModalOpen(true)} className="bg-red-600">
                <HangupIcon />
              </CallButton>
            </div>
          </div>

          <div className="absolute top-4 right-4 z-10 aspect-3/4 w-[25vw] max-w-40 min-w-20 overflow-hidden rounded-lg">
            {cameraEnabled ? (
              <LocalVideoTrack track={localCameraTrack} play={true} />
            ) : (
              <div className="relative h-full w-full bg-white">
                <UserIcon className="text-primary absolute top-1/2 left-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform" />
              </div>
            )}

            {!micEnabled && (
              <span className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-lg bg-white/50">
                <MicIcon className="h-4 w-3.75 shrink-0 text-black" enabled={false} />
              </span>
            )}
          </div>
        </div>
      </AgoraProvider>

      <Modal
        isOpen={isModalOpen}
        title={t('general.consultation.modal.finish_video_call')}
        actions={[
          { label: t('button.yes'), onClick: hangUp },
          { label: t('button.no'), onClick: () => setIsModalOpen(false), variant: 'outline' }
        ]}
      />
    </>
  )
}

// Export the AgoraManager component as the default export
export default AgoraManager
